'use client';

import type { AnswerState, AnswerValue } from '@repo/contracts/cardfinder/funnel/types';
import { useIdentityContext } from '@repo/platform-analytics/IdentityContext';
import { useRouter } from 'next/navigation';
import { type ReactNode, useEffect, useRef, useState, useTransition } from 'react';
import { AnswerStoreContext } from '@/entities/answer/model/answer-store-context';
import type { AnswerStore } from '@/entities/answer/model/types';
import { FunnelSessionContext, type FunnelSessionContextValue, type FunnelSessionUpdate } from '@/entities/session/model/funnel-session-context';
import type { FunnelSession } from '@/entities/session/model/types';
import { FINDER_ENTRY_PATH } from '@/shared/config/funnel/flowConstants';
import { isPastFirstQuestion } from '../in-funnel/utils/is-past-first-question';
import { emptyFunnelSession } from './funnel-session-reducer';
import { fetchLastCompleteSession, save } from './save-session';
import { loadFunnelSession, loadInput, removeInputAnswer, writeFunnelSession, writeInput, writeInputAnswer } from './storage';
export function FunnelSessionProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    isLoggedIn
  } = useIdentityContext();
  const router = useRouter();
  const [session, setSession] = useState<FunnelSession>(emptyFunnelSession);
  const [input, setInput] = useState<AnswerState>({});
  const [isHydrated, setIsHydrated] = useState(false);
  const [isPending, startTransition] = useTransition();
  const queue = useRef<Promise<void>>(Promise.resolve());
  const latestSession = useRef<FunnelSession>(emptyFunnelSession);
  useEffect(() => {
    // load the stored funnel session, or an empty one if none exists
    const stored = loadFunnelSession() ?? emptyFunnelSession;
    latestSession.current = stored;
    setSession(stored);

    // load any stored inputs
    const storedInput = loadInput() ?? stored.answers;
    setInput(storedInput);
    writeInput(storedInput);
    setIsHydrated(true);
  }, []);

  /** Replaces the funnel session and writes it to sessionStorage; in-progress input is left alone. */
  const commit = (next: FunnelSession): void => {
    latestSession.current = next;
    setSession(next);
    writeFunnelSession(next);
  };

  /** Replaces the funnel session and resets in-progress input to its answers. */
  const reset = (next_0: FunnelSession): void => {
    commit(next_0);
    setInput(next_0.answers);
    writeInput(next_0.answers);
  };

  /** Saves the funnel session to the server session API */
  const saveToApi = async (target: FunnelSession, funnelConfigVersion: string): Promise<FunnelSessionUpdate> => {
    const outcome = await save(target, isLoggedIn ? 'authenticated' : 'anonymous', funnelConfigVersion);
    switch (outcome.kind) {
      case 'saved':
        {
          const saved = {
            ...target,
            sessionId: outcome.sessionId,
            savedAs: outcome.savedAs
          };
          // A reset during the save made the saved funnel session obsolete; keep the newer one.
          if (latestSession.current === target) commit(saved);
          return {
            kind: 'continue',
            session: saved
          };
        }
      case 'start-over':
        reset(emptyFunnelSession);
        router.replace(FINDER_ENTRY_PATH);
        return {
          kind: 'start-over'
        };
      case 'unchanged':
        return {
          kind: 'continue',
          session: target
        };
    }
  };

  /** Queues and runs a task. Used to transition between questions. */
  const submit = (task: () => Promise<void>): void => {
    queue.current = queue.current.then(() => new Promise<void>(resolve => {
      startTransition(async () => {
        try {
          await task();
        } finally {
          resolve();
        }
      });
    }));
  };
  const sessionValue: FunnelSessionContextValue = {
    session,
    isPending,
    commit,
    reset,
    saveToApi,
    recover: fetchLastCompleteSession,
    submit
  };
  const setAnswer = (stepId: string, value: AnswerValue): void => {
    setInput(prev => ({
      ...prev,
      [stepId]: value
    }));
    writeInputAnswer(stepId, value);
  };
  const clearAnswer = (stepId_0: string): void => {
    setInput(prev_0 => {
      const {
        [stepId_0]: _removed,
        ...rest
      } = prev_0;
      return rest;
    });
    removeInputAnswer(stepId_0);
  };
  const store: AnswerStore = {
    answers: input,
    submittedAnswers: session.answers,
    isHydrated,
    shouldSkipRecommendations: !isPastFirstQuestion(session.answers)
  };
  return <FunnelSessionContext.Provider value={sessionValue}>
      <AnswerStoreContext.Provider value={{
      store,
      setAnswer,
      clearAnswer
    }}>
        {children}
      </AnswerStoreContext.Provider>
    </FunnelSessionContext.Provider>;
}